import React from 'react';
import { LockKeyholeIcon, MapPinIcon, RotateCcwIcon } from 'lucide-react';
import { RecoveryRules, defaultRecoveryRules, recoveryChannels, recoveryMarkets, rulesMatchDefaults } from '../data/recoveryRules';
type SubmissionRulesProps = {
  batchName: string;
  brand?: string;
  rules: RecoveryRules;
  // Only admins can lift a negotiated pricing lock.
  canUnlock?: boolean;
  // Preview mode: show the upload's rules without allowing edits.
  readOnly?: boolean;
  onChange: (rules: RecoveryRules) => void;
  onReset: () => void;
};
export function SubmissionRules({
  batchName,
  brand,
  rules,
  canUnlock = false,
  readOnly = false,
  onChange,
  onReset
}: SubmissionRulesProps) {
  const inherited = rulesMatchDefaults(rules, brand, batchName) || readOnly;
  const toggleChannel = (id: string) => onChange({
    ...rules,
    channels: rules.channels.includes(id) ? rules.channels.filter(channel => channel !== id) : [...rules.channels, id]
  });
  const toggleMarket = (market: string) => onChange({
    ...rules,
    markets: rules.markets.includes(market) ? rules.markets.filter(item => item !== market) : [...rules.markets, market]
  });
  return <section className="rounded-2xl border border-[#BB5E40]/20 bg-cream/50 p-5" aria-label={`Recovery rules for ${batchName}`} data-id="element-2337" data-mp="36crohl72eoz9">
      {!inherited && <div className="flex justify-end" data-id="element-2338" data-mp="31egglg0byojo">
          <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-2339" data-mp="2do2us78c9lxj">
            <RotateCcwIcon className="h-3 w-3" aria-hidden="true" data-id="element-2340" data-mp="2nfgp80lfnvjt" />
            Reset to defaults
          </button>
        </div>}

      <div className={`grid gap-6 lg:grid-cols-[1.1fr_0.9fr] ${inherited ? '' : 'mt-4'}`} data-id="element-2341" data-mp="1uxut7tkaf3jg">
        <div data-id="element-2342" data-mp="1pyrqwgvozbf6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2343" data-mp="1pz1ksfjuy2q5">Approved channels</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-2344" data-mp="3q1j79tz078jr">
            {recoveryChannels.map(channel => {
            const active = rules.channels.includes(channel.id);
            return <button key={channel.id} type="button" role="switch" aria-checked={active} onClick={() => toggleChannel(channel.id)} disabled={readOnly} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60'} ${readOnly ? 'cursor-default' : active ? '' : 'hover:border-[#BB5E40]/40'}`} data-id="element-2345" data-mp="3kwtimmzknvw">
                  {channel.iconUrl && <span aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${active ? 'bg-cream' : 'bg-ink/60'}`} style={{
                WebkitMaskImage: `url(${channel.iconUrl})`,
                maskImage: `url(${channel.iconUrl})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-2346" data-mp="vep71jca3vbb" />}
                  {channel.name}
                </button>;
          })}
          </div>
          {rules.channels.length === 0 && <p className="mt-3 text-xs font-medium text-[#94452E]" data-id="element-2347" data-mp="dpv9inq913cw">Select at least one channel or this upload cannot be activated.</p>}

        </div>

        <div className="space-y-5" data-id="element-2348" data-mp="2c2vkpe9v0peg">
          <RuleSlider label="Public pricing limit" detail="The maximum discount from MSRP at which this upload can be publicly listed." value={rules.indexPrice} min={0} max={90} suffix="% OFF MSRP" locked={readOnly || rules.indexLocked} onLockChange={canUnlock && !readOnly ? indexLocked => onChange({
          ...rules,
          indexLocked
        }) : undefined} onChange={indexPrice => onChange({
          ...rules,
          indexPrice
        })} data-id="element-2349" data-mp="282uznas99fyf" />
          <div data-id="element-2350" data-mp="1wfbcu46dfxar">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2351" data-mp="1weibjiq6m000">Market access</p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-2352" data-mp="2184jixlehfph">
              {recoveryMarkets.map(market => {
              const active = rules.markets.includes(market);
              return <button key={market} type="button" role="switch" aria-checked={active} onClick={() => toggleMarket(market)} disabled={readOnly} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60'} ${readOnly ? 'cursor-default' : active ? '' : 'hover:border-[#BB5E40]/40'}`} data-id="element-2353" data-mp="1rh9vposia81x">
                    <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2354" data-mp="1hte8jc1t1fu4" />
                    {market}
                  </button>;
            })}
            </div>
            <p className="mt-2 text-xs text-ink/50" data-id="element-2355" data-mp="1csgv1crqp9yy">
              {rules.markets.length === 0 ? 'Select at least one market where this inventory can be sold.' : 'Regions where this upload can be marketed and sold.'}
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function RuleSlider({
  label,
  detail,
  value,
  min,
  max,
  suffix,
  locked = false,
  onLockChange,
  onChange
}: {
  label: string;
  detail: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  locked?: boolean;
  onLockChange?: (locked: boolean) => void;
  onChange: (value: number) => void;
}) {
  const id = `rule-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const lockId = `${id}-lock`;
  return <div data-id="element-2356" data-mp="16ztbgjm8x544">
      <div className="flex items-baseline justify-between gap-3" data-id="element-2357" data-mp="16zkxspiatvrv">
        <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2358" data-mp="1bye15p3vg6eb">{label}</label>
        <span className={`text-sm font-semibold tabular-nums ${locked ? 'text-ink/55' : 'text-ink'}`} data-id="element-2359" data-mp="1gvwctn99wzq0">
          {value}
          <span className="ml-1 text-xs font-medium text-ink/50" data-id="element-2360" data-mp="1lwk0q027h0g0">{suffix}</span>
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} value={value} disabled={locked} onChange={event => onChange(Number(event.target.value))} className={`mt-2 w-full accent-[#BB5E40] ${locked ? 'cursor-not-allowed opacity-60' : ''}`} data-id="element-2361" data-mp="1pwujkdb3jlca" />
      {onLockChange && <div className="mt-2 flex items-center gap-2" data-id="element-2362" data-mp="3fergcggc0tnb">
          <input id={lockId} type="checkbox" checked={locked} onChange={event => onLockChange(event.target.checked)} className="h-3.5 w-3.5 accent-[#BB5E40]" data-id="element-2363" data-mp="3fd7zooqrkgl0" />
          <label htmlFor={lockId} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-ink/65" data-id="element-2364" data-mp="36c9s5wh49qxo">
            <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-2365" data-mp="1zczbmr6n1rf" />
            Lock
          </label>
        </div>}
      <p className={`mt-1 text-xs ${locked ? 'font-medium text-[#94452E]' : 'text-ink/50'}`} data-id="element-2366" data-mp="3u5i85ohxbsqf">
        {locked ? `Locked at the agreed ${value}% off MSRP.${onLockChange ? ' Unlock to adjust.' : ' Contact ManyCo to change your agreement.'}` : detail}
      </p>
    </div>;
}
export { defaultRecoveryRules };